import { createLogger } from "../logger";

const logger = createLogger("ALERT_SOUND");

type AlertSoundListener = (playing: boolean, alertUrl?: string) => void;

class AlertSoundManager {
  private audio: HTMLAudioElement | null = null;
  private playing = false;
  private pendingAlert: { alertId: string; url: string } | null = null;
  private listeners: Set<AlertSoundListener> = new Set();
  private unlocked = false;

  /**
   * Pre-unlock audio on first user click so that subsequent
   * programmatic play() calls are not blocked by autoplay policy.
   */
  initUserGesture(): void {
    if (this.unlocked) return;

    const unlock = () => {
      if (this.unlocked) return;
      this.unlocked = true;

      const audio = this.getOrCreateAudio();
      audio.volume = 0;
      audio.play().then(() => {
        audio.pause();
        audio.volume = 1;
        audio.currentTime = 0;
        logger.log("Audio unlocked via user gesture");

        // If there was a pending alert waiting for unlock, play it now
        if (this.pendingAlert) {
          const { alertId, url } = this.pendingAlert;
          logger.log("Playing pending alert after unlock", alertId);
          this.playInternal(alertId, url);
        }
      }).catch(() => {
        // Still locked, will retry on next click
        this.unlocked = false;
      });

      document.removeEventListener("click", unlock);
    };

    document.addEventListener("click", unlock, { once: true });
  }

  play(alertId: string, url = "/monitoring"): void {
    // If already playing, ignore duplicate
    if (this.playing) return;

    this.playInternal(alertId, url);
  }

  private playInternal(alertId: string, url: string): void {
    const audio = this.getOrCreateAudio();
    audio.loop = true;
    audio.volume = 1;
    audio.currentTime = 0;

    audio.play().then(() => {
      this.playing = true;
      this.pendingAlert = { alertId, url };
      this.notify();
      logger.log("Alert sound playing", alertId);
    }).catch(() => {
      // Autoplay blocked - store as pending
      logger.warn("Autoplay blocked, storing as pending alert");
      this.playing = false;
      this.pendingAlert = { alertId, url };
      this.notify();
    });
  }

  stop(): void {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
    }
    this.playing = false;
    this.pendingAlert = null;
    this.notify();
    logger.log("Alert sound stopped");
  }

  isPlaying(): boolean {
    return this.playing;
  }

  getPendingAlert(): { alertId: string; url: string } | null {
    return this.pendingAlert;
  }

  subscribe(callback: AlertSoundListener): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  private notify(): void {
    const url = this.pendingAlert?.url;
    this.listeners.forEach((cb) => cb(this.playing, url));
  }

  private getOrCreateAudio(): HTMLAudioElement {
    if (!this.audio) {
      this.audio = new Audio("/sounds/critical.wav");
      this.audio.preload = "auto";
    }
    return this.audio;
  }
}

export const alertSoundManager = new AlertSoundManager();
